import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import { ScrollService } from '@fcom/common/services';
import { WindowRef } from '@fcom/core/providers';
import { CorporateService } from '@fcom/common/services/corporate/corporate.service';
import { AppState } from '@fcom/core/interfaces';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { EMAIL_REGEX, NAME_PATTERN, unsubscribe } from '@fcom/core/utils';
import { Profile } from '@fcom/core-api/login';
import { finShare } from '@fcom/rx';
import { IconPosition } from '@fcom/ui-components';

interface SafDownloadFormData {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  company: FormControl<string>;
  email: FormControl<string>;
}

@Component({
  selector: 'fin-saf-download-form',
  templateUrl: './saf-download-form.component.html',
})
export class SafDownloadFormComponent implements OnInit, OnDestroy {
  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  readonly PDF_URL =
    'https://cms.finnair.com/resource/blob/3389664/51a586a8f6734aba4ca0e29ba4b10cad/The%20Quick%20Guide%20to%20SAF-%20Finnair%20May%202024.pdf';

  form: FormGroup<SafDownloadFormData>;
  profile$: Observable<Profile>;
  subscription = new Subscription();

  constructor(
    private corporateService: CorporateService,
    private scrollService: ScrollService,
    private store$: Store<AppState>,
    private elementRef: ElementRef,
    private windowRef: WindowRef
  ) {}

  ngOnInit(): void {
    this.form = new FormGroup({
      firstName: new FormControl('', [Validators.required, Validators.pattern(NAME_PATTERN)]),
      lastName: new FormControl('', [Validators.required, Validators.pattern(NAME_PATTERN)]),
      company: new FormControl('', Validators.required),
      email: new FormControl('', [Validators.required, Validators.pattern(EMAIL_REGEX)]),
    });
    this.profile$ = this.store$.pipe(profileOrUndefinedIfNotLoggedIn(), finShare());
    this.prefillForm();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  handleDownload(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.scrollService.scrollToFirstInvalidInput(this.elementRef);
      return;
    }
    this.windowRef.nativeWindow.open(this.PDF_URL, '_blank');
    this.subscription.add(
      this.profile$
        .pipe(
          switchMap((userProfile) => {
            return this.corporateService.sendContactCaseToSalesForce(
              {
                subject: 'Lead - SAF info package download',
                message: 'For information only',
                reason: 'SAF Download',
                companyName: this.form.controls.company.value,
                email: this.form.controls.email.value,
                firstname: this.form.controls.firstName.value,
                lastname: this.form.controls.lastName.value,
              },
              userProfile
            );
          }),
          take(1)
        )
        .subscribe()
    );
  }

  private prefillForm(): void {
    this.subscription.add(
      this.profile$.pipe(filter(Boolean)).subscribe((userProfile) => {
        this.form.controls.firstName.setValue(userProfile.firstname);
        this.form.controls.lastName.setValue(userProfile.lastname);
        this.form.controls.email.setValue(userProfile.email);
        this.form.controls.company.setValue(userProfile.corporate?.name);
      })
    );
  }
}
